/**
 * Abrir uma condicional — consignação de balcão: o cliente leva a peça pra
 * casa pra decidir, sem venda fechada ainda.
 *
 * ROTA PRÓPRIA, e não gaveta: é o mesmo catálogo com busca, chips de
 * categoria e seletor de cor/tamanho do PDV (`TelaPDV.tsx`), e um catálogo
 * inteiro dentro de uma gaveta de 560px seria o formulário denso que a spec
 * evita — a mesma razão de `TelaNovoLancamento` ter rota própria em vez de
 * gaveta.
 *
 * Duas diferenças do carrinho do PDV, deliberadas:
 *
 * 1. **Sem preço editável.** `ModalEscolha` aqui não tem campo de valor — a
 *    condicional não é o lugar de negociar desconto (isso acontece na hora de
 *    faturar, na venda de verdade). O preço que viaja é sempre o de tabela.
 * 2. **Sem pagamento.** Não há `Fechamento` nesta tela: a condicional não
 *    cobra nada, só reserva a peça. `POST /condicionais` devolve o
 *    subtotal para a pessoa conferir, e é isso.
 *
 * O crediário do cliente selecionado aparece com o que a API já traz —
 * liberado, limite e parcelas — pela mesma razão do `Fechamento` do PDV: é
 * conveniência de UX, não autorização (CONSTITUICAO.md §4.1). O "quanto já
 * está comprometido considerando outras condicionais abertas" é cálculo NOVO
 * do servidor que a busca de cliente não expõe — esta tela não inventa esse
 * número; ela deixa a mensagem do 422 (que já vem pronta, dizendo quanto há
 * disponível) aparecer quando o servidor recusar.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Botao, BotaoIcone } from '../../componentes/Botao'
import { CampoData } from '../../componentes/CampoData'
import { mostrarAviso } from '../../componentes/avisos'
import { usarFocoPreso } from '../../componentes/foco'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { abrirCondicional, TAMANHO_MAXIMO_OBSERVACAO } from '../../plataforma/condicional'
import { carregarClientes, type LinhaCliente } from '../../plataforma/clientes'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { mascararDocumento } from '../../plataforma/mascaras'
import {
  carregarCatalogo,
  carregarFicha,
  type Catalogo,
  type Categoria,
  type Ficha,
  type LinhaProduto,
} from '../../plataforma/produtos'
import { LayoutApp } from '../app/LayoutApp'

/** Um item do carrinho da condicional — mesmo shape do carrinho do PDV, sem
 *  override de preço (a condicional não tem esse campo). */
type ItemCarrinho = {
  chave: string
  codigo: string
  codigoBarras: string
  nome: string
  cor: string
  tamanho: string
  precoCentavos: number
  quantidade: number
  saldoMaximo: number
}

type ErroDaAbertura = {
  campos: Record<string, string>
  mensagem: string
}

export function TelaAbrirCondicional() {
  const navegar = useNavigate()

  /* ---------------------------------------------------------- catálogo */
  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [categoria, setCategoria] = useState<Categoria | ''>('')
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null)
  const [erroCatalogo, setErroCatalogo] = useState('')

  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()
    carregarCatalogo({ busca: buscaAtiva, categoria }, controle.signal)
      .then((dados) => {
        setCatalogo(dados)
        setErroCatalogo('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErroCatalogo(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o catálogo.',
        )
      })
    return () => controle.abort()
  }, [buscaAtiva, categoria])

  /* ------------------------------------------------------------ carrinho */
  const [carrinho, setCarrinho] = useState<ItemCarrinho[]>([])

  const definirQuantidade = useCallback((chave: string, quantidade: number) => {
    setCarrinho((atual) => {
      if (quantidade <= 0) return atual.filter((item) => item.chave !== chave)
      return atual.map((item) =>
        item.chave === chave
          ? { ...item, quantidade: Math.min(quantidade, item.saldoMaximo) }
          : item,
      )
    })
  }, [])

  const removerItem = useCallback((chave: string) => {
    setCarrinho((atual) => atual.filter((item) => item.chave !== chave))
  }, [])

  const totalItens = carrinho.reduce((soma, item) => soma + item.quantidade, 0)
  const subtotalCentavos = carrinho.reduce(
    (soma, item) => soma + item.precoCentavos * item.quantidade,
    0,
  )

  /* --------------------------------------------------- seleção de variação */
  const [escolhendo, setEscolhendo] = useState<LinhaProduto | null>(null)
  const [ficha, setFicha] = useState<Ficha | null>(null)
  const [erroFicha, setErroFicha] = useState('')
  const [corEscolhida, setCorEscolhida] = useState('')
  const [tamanhoEscolhido, setTamanhoEscolhido] = useState('')
  const [quantidadeEscolhida, setQuantidadeEscolhida] = useState(1)

  const corAtual = ficha?.cores.find((c) => c.cor === corEscolhida)
  const indiceTamanhoAtual = ficha ? ficha.tamanhos.indexOf(tamanhoEscolhido) : -1
  const celulaAtual =
    corAtual && indiceTamanhoAtual >= 0 ? corAtual.celulas[indiceTamanhoAtual] : undefined

  const abrirEscolha = useCallback(async (produto: LinhaProduto) => {
    setEscolhendo(produto)
    setFicha(null)
    setErroFicha('')
    setCorEscolhida('')
    setTamanhoEscolhido('')
    setQuantidadeEscolhida(1)
    try {
      const dados = await carregarFicha(produto.codigo)
      setFicha(dados)
      setCorEscolhida(dados.cores[0]?.cor ?? '')
      setTamanhoEscolhido(dados.tamanhos.length === 1 ? (dados.tamanhos[0] ?? '') : '')
    } catch (causa: unknown) {
      setErroFicha(
        causa instanceof ErroApi ? causa.message : 'Não foi possível carregar a ficha do produto.',
      )
    }
  }, [])

  const fecharEscolha = useCallback(() => {
    setEscolhendo(null)
    setFicha(null)
    setErroFicha('')
  }, [])

  useEffect(() => {
    if (!escolhendo) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') fecharEscolha()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [escolhendo, fecharEscolha])

  const trocarCorEscolha = useCallback((novaCor: string) => {
    setCorEscolhida(novaCor)
    setTamanhoEscolhido('')
    setQuantidadeEscolhida(1)
  }, [])

  const trocarTamanhoEscolha = useCallback((novoTamanho: string) => {
    setTamanhoEscolhido(novoTamanho)
    setQuantidadeEscolhida(1)
  }, [])

  const incrementarQuantidade = useCallback(() => {
    setQuantidadeEscolhida((q) => (celulaAtual ? Math.min(celulaAtual.saldo, q + 1) : q))
  }, [celulaAtual])

  const decrementarQuantidade = useCallback(() => {
    setQuantidadeEscolhida((q) => Math.max(1, q - 1))
  }, [])

  const adicionarAoCarrinho = useCallback(() => {
    if (
      !ficha ||
      !corAtual ||
      !celulaAtual ||
      !celulaAtual.existe ||
      celulaAtual.saldo <= 0 ||
      !celulaAtual.codigo_barras
    ) {
      return
    }
    const quantidade = Math.max(1, Math.min(quantidadeEscolhida, celulaAtual.saldo))
    const chave = `${ficha.codigo}|${corAtual.cor}|${tamanhoEscolhido}`

    setCarrinho((atual) => {
      const jaExiste = atual.some((item) => item.chave === chave)
      if (jaExiste) {
        return atual.map((item) =>
          item.chave === chave
            ? { ...item, quantidade: Math.min(item.quantidade + quantidade, celulaAtual.saldo) }
            : item,
        )
      }
      return [
        ...atual,
        {
          chave,
          codigo: ficha.codigo,
          codigoBarras: celulaAtual.codigo_barras ?? '',
          nome: ficha.nome,
          cor: corAtual.cor,
          tamanho: tamanhoEscolhido,
          precoCentavos: celulaAtual.preco_centavos,
          quantidade,
          saldoMaximo: celulaAtual.saldo,
        },
      ]
    })
    fecharEscolha()
  }, [ficha, corAtual, celulaAtual, quantidadeEscolhida, tamanhoEscolhido, fecharEscolha])

  /* ---------------------------------------------------------------- cliente */
  const [clienteBusca, setClienteBusca] = useState('')
  const [clienteBuscaAtiva, setClienteBuscaAtiva] = useState('')
  const [clientesEncontrados, setClientesEncontrados] = useState<LinhaCliente[]>([])
  const [clienteSelecionado, setClienteSelecionado] = useState<LinhaCliente | null>(null)

  useEffect(() => {
    const relogio = setTimeout(() => setClienteBuscaAtiva(clienteBusca), 300)
    return () => clearTimeout(relogio)
  }, [clienteBusca])

  useEffect(() => {
    if (!clienteBuscaAtiva) {
      setClientesEncontrados([])
      return
    }
    const controle = new AbortController()
    carregarClientes(clienteBuscaAtiva, 'todos', controle.signal)
      .then((dados) => setClientesEncontrados(dados.linhas))
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setClientesEncontrados([])
      })
    return () => controle.abort()
  }, [clienteBuscaAtiva])

  const selecionarCliente = useCallback((cliente: LinhaCliente) => {
    setClienteSelecionado(cliente)
    setClienteBusca('')
    setClienteBuscaAtiva('')
    setClientesEncontrados([])
  }, [])

  /* -------------------------------------------------------------- prazo */
  const [prazo, setPrazo] = useState('')
  const [observacao, setObservacao] = useState('')

  /* ------------------------------------------------------------ enviar */
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<ErroDaAbertura | null>(null)

  useEffect(() => {
    setErro(null)
  }, [carrinho, clienteSelecionado])

  const razaoCrediario = !clienteSelecionado
    ? 'Selecione o cliente que vai levar a peça.'
    : totalItens === 0
      ? 'Adicione ao menos uma peça ao carrinho.'
      : !clienteSelecionado.crediario.liberado
        ? `${clienteSelecionado.nome} não está com crediário liberado — condicional exige crediário liberado.`
        : ''

  const pronto = razaoCrediario === '' && !enviando

  const abrir = useCallback(async () => {
    if (!clienteSelecionado || !pronto) return
    setEnviando(true)
    setErro(null)
    try {
      const criada = await abrirCondicional({
        cliente: clienteSelecionado.codigo,
        itens: carrinho.map((item) => ({ sku: item.codigoBarras, quantidade: item.quantidade })),
        prazo: prazo || undefined,
        observacao: observacao.trim() || undefined,
      })
      mostrarAviso(`Condicional ${criada.codigo} aberta para ${criada.cliente.nome}.`)
      navegar('/app/condicionais')
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setErro({ campos: {}, mensagem: 'Não foi possível abrir a condicional.' })
        return
      }
      setErro({ campos: causa.campos, mensagem: causa.message })
    } finally {
      setEnviando(false)
    }
  }, [clienteSelecionado, pronto, carrinho, prazo, observacao, navegar])

  const chaveDoItemComErro = Object.keys(erro?.campos ?? {}).find((chave) =>
    /^item_\d+$/.test(chave),
  )
  const indiceItemComErro = chaveDoItemComErro
    ? Number(chaveDoItemComErro.slice('item_'.length)) - 1
    : -1
  const camposDoErro = erro?.campos ?? {}
  const erroObservacao = camposDoErro.observacao ? (erro?.mensagem ?? '') : ''
  const erroPrazo = camposDoErro.prazo ? (erro?.mensagem ?? '') : ''
  const erroCliente = camposDoErro.cliente ? (erro?.mensagem ?? '') : ''
  const erroGeral =
    erro && indiceItemComErro < 0 && !erroObservacao && !erroPrazo && !erroCliente
      ? erro.mensagem
      : ''

  const chipsCategoria: { chave: Categoria | ''; rotulo: string; total?: number }[] = [
    { chave: '', rotulo: 'Todas' },
    ...(catalogo?.categorias.map((c) => ({ chave: c.chave, rotulo: c.rotulo, total: c.produtos })) ??
      []),
  ]

  return (
    <LayoutApp
      titulo="Nova condicional"
      subtitulo="Escolha o cliente e as peças que saem para decisão."
      trilha={[{ rotulo: 'Condicional', para: '/app/condicionais' }, { rotulo: 'Nova condicional' }]}
      voltarPara="/app/condicionais"
      largura="total"
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_400px] lg:items-start">
        <section className="flex min-w-0 flex-col gap-3.5">
          <label className="composto h-[42px] cursor-text">
            <Icone nome="barcode_scanner" tamanho={19} className="text-texto-fraco" />
            <input
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Bipe o código de barras ou digite o nome do produto"
              autoFocus
            />
            <span className="sr-only">Buscar produto</span>
          </label>

          <div className="flex flex-wrap gap-2">
            {chipsCategoria.map((chip) => {
              const ativo = chip.chave === categoria
              return (
                <button
                  key={chip.chave || 'todas'}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => setCategoria(chip.chave)}
                  className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] font-semibold ${
                    ativo
                      ? 'border-marca bg-marca-suave text-marca-forte'
                      : 'border-borda bg-superficie text-tinta'
                  }`}
                >
                  {chip.rotulo}
                  {chip.total !== undefined && (
                    <span
                      className={`tabular rounded-full px-[6px] py-[1px] text-[11px] font-bold ${
                        ativo ? 'bg-marca-barra text-marca-forte' : 'bg-divisoria text-texto-suave'
                      }`}
                    >
                      {chip.total}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {erroCatalogo && (
            <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
              {erroCatalogo}
            </div>
          )}

          <div className="max-h-[62vh] overflow-y-auto pr-1">
            {!catalogo ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-[176px] animate-pulse rounded-sistema bg-superficie" />
                ))}
              </div>
            ) : catalogo.produtos.length === 0 ? (
              <div className="flex flex-col items-center gap-2.5 px-4 py-16 text-center">
                <Icone nome="search" tamanho={26} className="text-inativo" />
                <p className="text-[13.5px] font-semibold text-tinta">Nada encontrado</p>
                <p className="max-w-[320px] text-[12.5px] text-texto-suave">
                  Confira o código ou busque pelo nome.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
                {catalogo.produtos.map((produto) => (
                  <CartaoProduto
                    key={produto.codigo}
                    produto={produto}
                    aoEscolher={() => void abrirEscolha(produto)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="flex max-h-[85vh] flex-col rounded-sistema border border-borda bg-superficie lg:sticky lg:top-6">
          <BlocoCliente
            clienteSelecionado={clienteSelecionado}
            busca={clienteBusca}
            aoBuscar={setClienteBusca}
            resultados={clientesEncontrados}
            aoSelecionar={selecionarCliente}
            aoRemover={() => setClienteSelecionado(null)}
          />

          {erroCliente && (
            <div
              role="alert"
              className="mx-4 mt-3 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-erro-texto"
            >
              {erroCliente}
            </div>
          )}

          {erroGeral && (
            <div
              role="alert"
              className="mx-4 mt-3 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-erro-texto"
            >
              {erroGeral}
            </div>
          )}

          <ListaCarrinho
            itens={carrinho}
            aoMudarQuantidade={definirQuantidade}
            aoRemover={removerItem}
            indiceComErro={indiceItemComErro}
            mensagemDoErro={erro?.mensagem ?? ''}
          />

          <div className="flex flex-col gap-3.5 border-t border-borda p-4">
            <div className="flex items-baseline justify-between">
              <span className="text-[13px] text-texto-suave">
                {totalItens} {totalItens === 1 ? 'peça' : 'peças'}
              </span>
              <span className="tabular text-[20px] font-bold text-tinta">
                {formatarMoeda(subtotalCentavos)}
              </span>
            </div>

            <CampoData
              rotulo="Prazo para decidir (opcional)"
              value={prazo}
              onChange={setPrazo}
              erro={erroPrazo}
            />

            <div>
              <label htmlFor="observacao-condicional" className="mb-2 block text-[13px] font-semibold text-texto">
                Observação (opcional)
              </label>
              <textarea
                id="observacao-condicional"
                rows={2}
                maxLength={TAMANHO_MAXIMO_OBSERVACAO}
                value={observacao}
                onChange={(evento) => setObservacao(evento.target.value)}
                placeholder="Combinado com o cliente, se houver"
                aria-invalid={erroObservacao ? true : undefined}
                className="campo w-full px-3 py-2 text-[13px]"
              />
              {erroObservacao && (
                <p role="alert" className="mt-1.5 text-[12px] font-medium text-erro">
                  {erroObservacao}
                </p>
              )}
            </div>

            <button
              type="button"
              disabled={!pronto}
              onClick={() => void abrir()}
              className={`flex h-[44px] w-full items-center justify-center gap-2 rounded-sistema text-sm font-bold text-white ${
                pronto ? 'bg-tinta hover:bg-titulo' : 'cursor-not-allowed bg-inativo'
              }`}
            >
              <Icone nome={pronto ? 'apparel' : 'lock'} tamanho={19} />
              {enviando ? 'Abrindo…' : 'Abrir condicional'}
            </button>

            <p role="status" className="text-[12px] leading-[1.5] text-texto-fraco">
              {razaoCrediario ||
                'A peça sai do estoque agora. Ela volta ao faturar (vira venda) ou ao devolver.'}
            </p>
          </div>
        </aside>
      </div>

      {escolhendo && (
        <ModalEscolha
          produto={escolhendo}
          ficha={ficha}
          erro={erroFicha}
          cor={corEscolhida}
          tamanho={tamanhoEscolhido}
          quantidade={quantidadeEscolhida}
          aoTrocarCor={trocarCorEscolha}
          aoTrocarTamanho={trocarTamanhoEscolha}
          aoIncrementar={incrementarQuantidade}
          aoDecrementar={decrementarQuantidade}
          aoFechar={fecharEscolha}
          aoAdicionar={adicionarAoCarrinho}
        />
      )}
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ peças */

function CartaoProduto({
  produto,
  aoEscolher,
}: {
  produto: LinhaProduto
  aoEscolher: () => void
}) {
  const semEstoque = produto.pecas === 0

  return (
    <button
      type="button"
      onClick={aoEscolher}
      aria-label={`Ver variações de ${produto.nome}`}
      className="flex flex-col overflow-hidden rounded-sistema border border-borda bg-superficie text-left"
    >
      <div className="relative flex h-[104px] items-center justify-center bg-divisoria text-inativo">
        <Icone nome="image" tamanho={26} />
        <span
          className={`tabular absolute top-2 right-2 rounded-full px-2 py-[3px] text-[11px] font-bold text-white ${
            semEstoque ? 'bg-erro' : 'bg-[rgba(31,41,55,0.82)]'
          }`}
        >
          {semEstoque ? 'sem estoque' : `${produto.pecas} un`}
        </span>
      </div>
      <div className="p-3">
        <div className="min-h-[34px] text-[13px] font-semibold text-tinta">{produto.nome}</div>
        <div className="mt-1.5 flex items-center justify-between">
          <span className="tabular text-[15px] font-bold text-tinta">
            {formatarMoeda(produto.preco_centavos)}
          </span>
          <span className="flex">
            {produto.cores.slice(0, 3).map((cor, i) => (
              <span
                key={cor.nome}
                aria-hidden
                style={{ background: cor.hex ?? '#D1D5DB', marginLeft: i ? -4 : 0 }}
                className="h-3.5 w-3.5 rounded-full border-2 border-superficie"
              />
            ))}
          </span>
        </div>
      </div>
    </button>
  )
}

function BlocoCliente({
  clienteSelecionado,
  busca,
  aoBuscar,
  resultados,
  aoSelecionar,
  aoRemover,
}: {
  clienteSelecionado: LinhaCliente | null
  busca: string
  aoBuscar: (texto: string) => void
  resultados: LinhaCliente[]
  aoSelecionar: (cliente: LinhaCliente) => void
  aoRemover: () => void
}) {
  if (clienteSelecionado) {
    return (
      <div className="border-b border-borda p-3.5">
        <div className="flex items-center gap-2.5">
          <Icone nome="person" tamanho={20} className="text-marca" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13.5px] font-semibold text-tinta">
              {clienteSelecionado.nome}
            </div>
            <div className="tabular text-xs text-texto-suave">
              {clienteSelecionado.documento
                ? mascararDocumento(clienteSelecionado.tipo, clienteSelecionado.documento)
                : clienteSelecionado.tipo_rotulo}
            </div>
          </div>
          <button
            type="button"
            onClick={aoRemover}
            className="h-[34px] shrink-0 rounded-sistema border border-borda px-3 text-[12.5px] font-semibold text-texto"
          >
            Trocar
          </button>
        </div>

        {/*
          Conveniência de UX (CONSTITUICAO.md §4.1): mostra o que a busca de
          cliente já traz — liberado, limite, parcelas. O "quanto já está
          comprometido com outras condicionais abertas" é conta NOVA do
          servidor que este bloco não tem; a tela não inventa esse número.
        */}
        <div
          className={`mt-3 rounded-sistema border px-3.5 py-2.5 text-[12px] leading-[1.5] ${
            clienteSelecionado.crediario.liberado
              ? 'border-marca-barra bg-marca-fundo-2 text-marca-forte'
              : 'border-borda bg-superficie-suave text-texto-suave'
          }`}
        >
          {clienteSelecionado.crediario.liberado ? (
            <>
              Crediário liberado até {formatarMoeda(clienteSelecionado.crediario.limite_centavos)},
              em até {clienteSelecionado.crediario.parcelas_max}×. O quanto já está comprometido
              (dívida em aberto e outras condicionais) é conferido pelo servidor ao abrir.
            </>
          ) : (
            'Sem crediário liberado — condicional exige crediário liberado neste cliente.'
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative border-b border-borda p-3.5">
      <label className="composto h-[38px] cursor-text">
        <Icone nome="person_search" tamanho={18} className="text-texto-fraco" />
        <input
          value={busca}
          onChange={(evento) => aoBuscar(evento.target.value)}
          placeholder="Buscar cliente por nome, CPF ou telefone"
        />
        <span className="sr-only">Buscar cliente</span>
      </label>

      {busca && (
        <div className="absolute top-[58px] right-3.5 left-3.5 z-10 max-h-[220px] overflow-y-auto rounded-sistema border border-borda bg-superficie shadow-[0_12px_32px_rgba(15,23,42,0.12)]">
          {resultados.length === 0 ? (
            <p className="px-3.5 py-3 text-[12.5px] text-texto-suave">Nenhum cliente encontrado.</p>
          ) : (
            resultados.map((cliente) => (
              <button
                key={cliente.codigo}
                type="button"
                onClick={() => aoSelecionar(cliente)}
                className="flex w-full flex-col items-start gap-0.5 border-t border-divisoria px-3.5 py-2.5 text-left first:border-t-0 hover:bg-fundo"
              >
                <span className="text-[13px] font-semibold text-tinta">{cliente.nome}</span>
                <span className="tabular text-xs text-texto-suave">
                  {cliente.documento
                    ? mascararDocumento(cliente.tipo, cliente.documento)
                    : cliente.tipo_rotulo}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function ListaCarrinho({
  itens,
  aoMudarQuantidade,
  aoRemover,
  indiceComErro,
  mensagemDoErro,
}: {
  itens: ItemCarrinho[]
  aoMudarQuantidade: (chave: string, quantidade: number) => void
  aoRemover: (chave: string) => void
  indiceComErro: number
  mensagemDoErro: string
}) {
  if (itens.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2.5 px-6 py-10 text-center">
        <Icone nome="apparel" tamanho={24} className="text-inativo" />
        <p className="text-[13.5px] font-semibold text-tinta">Nenhuma peça na condicional</p>
        <p className="text-[12.5px] text-texto-suave">
          Toque em um produto no catálogo para adicionar.
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {itens.map((item, indice) => (
        <div
          key={item.chave}
          className={`border-b border-divisoria px-4 py-3 ${
            indice === indiceComErro ? 'bg-erro-suave/40' : ''
          }`}
        >
          <div className="flex items-start gap-2.5">
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold text-tinta">{item.nome}</div>
              <div className="tabular mt-0.5 text-xs text-texto-suave">
                {item.cor} · {item.tamanho} · {formatarMoeda(item.precoCentavos)}
              </div>
            </div>
            <span className="tabular font-bold whitespace-nowrap text-tinta">
              {formatarMoeda(item.precoCentavos * item.quantidade)}
            </span>
            <button
              type="button"
              aria-label={`Remover ${item.nome} da condicional`}
              onClick={() => aoRemover(item.chave)}
              className="text-inativo"
            >
              <Icone nome="close" tamanho={17} />
            </button>
          </div>

          <div className="mt-2.5 inline-flex items-center overflow-hidden rounded-sistema border border-borda">
            <button
              type="button"
              aria-label={`Diminuir quantidade de ${item.nome}`}
              onClick={() => aoMudarQuantidade(item.chave, item.quantidade - 1)}
              className="flex h-8 w-8 items-center justify-center text-texto"
            >
              −
            </button>
            <span className="tabular flex h-8 w-10 items-center justify-center border-x border-borda text-[13px] font-bold">
              {item.quantidade}
            </span>
            <button
              type="button"
              aria-label={`Aumentar quantidade de ${item.nome}`}
              disabled={item.quantidade >= item.saldoMaximo}
              onClick={() => aoMudarQuantidade(item.chave, item.quantidade + 1)}
              className="flex h-8 w-8 items-center justify-center text-texto disabled:text-inativo"
            >
              +
            </button>
          </div>
          {item.quantidade >= item.saldoMaximo && (
            <p className="mt-1.5 text-[11.5px] text-texto-fraco">Limite do estoque disponível.</p>
          )}

          {indice === indiceComErro && mensagemDoErro && (
            <p
              role="alert"
              className="mt-2 flex items-start gap-1.5 text-[12px] leading-[1.5] font-medium text-erro"
            >
              <Icone nome="error" tamanho={15} />
              {mensagemDoErro}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

function ModalEscolha({
  produto,
  ficha,
  erro,
  cor,
  tamanho,
  quantidade,
  aoTrocarCor,
  aoTrocarTamanho,
  aoIncrementar,
  aoDecrementar,
  aoFechar,
  aoAdicionar,
}: {
  produto: LinhaProduto
  ficha: Ficha | null
  erro: string
  cor: string
  tamanho: string
  quantidade: number
  aoTrocarCor: (cor: string) => void
  aoTrocarTamanho: (tamanho: string) => void
  aoIncrementar: () => void
  aoDecrementar: () => void
  aoFechar: () => void
  aoAdicionar: () => void
}) {
  const painel = useRef<HTMLDivElement>(null)

  const corObjeto = ficha?.cores.find((c) => c.cor === cor)
  const indiceTamanho = ficha ? ficha.tamanhos.indexOf(tamanho) : -1
  const celula = corObjeto && indiceTamanho >= 0 ? corObjeto.celulas[indiceTamanho] : undefined
  const semCodigoBarras = !!celula && celula.existe && !celula.codigo_barras
  const podeAdicionar =
    !!celula && celula.existe && celula.saldo > 0 && quantidade > 0 && !semCodigoBarras

  usarFocoPreso(painel)

  return (
    <>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={aoFechar}
        className="fixed inset-0 z-40 cursor-default bg-[rgba(15,23,42,0.45)]"
      />
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label={`Escolher variação de ${produto.nome}`}
        tabIndex={-1}
        className="fixed top-1/2 left-1/2 z-50 w-[520px] max-w-[94vw] -translate-x-1/2 -translate-y-1/2 rounded-card bg-superficie shadow-[0_24px_64px_rgba(15,23,42,0.28)]"
      >
        <div className="flex items-center gap-3.5 border-b border-borda p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sistema bg-divisoria text-inativo">
            <Icone nome="image" tamanho={22} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-titulo text-[16px] font-semibold text-tinta">{produto.nome}</div>
            <div className="tabular mt-0.5 text-[12.5px] text-texto-suave">
              {produto.codigo} · {formatarMoeda(produto.preco_centavos)}
            </div>
          </div>
          <BotaoIcone icone="close" rotulo="Fechar" tom="suave" onClick={aoFechar} />
        </div>

        <div className="p-5">
          {erro && (
            <div role="alert" className="mb-4 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
              {erro}
            </div>
          )}

          {!ficha && !erro && (
            <div className="flex flex-col gap-3">
              <div className="h-8 w-2/3 animate-pulse rounded-sistema bg-divisoria" />
              <div className="h-8 w-1/2 animate-pulse rounded-sistema bg-divisoria" />
            </div>
          )}

          {ficha && (
            <>
              <p className="mb-2.5 text-[12.5px] font-semibold text-texto">Cor</p>
              <div className="mb-5 flex flex-wrap gap-2">
                {ficha.cores.map((c) => {
                  const ativo = c.cor === cor
                  return (
                    <button
                      key={c.cor}
                      type="button"
                      aria-pressed={ativo}
                      onClick={() => aoTrocarCor(c.cor)}
                      className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-[13px] font-semibold ${
                        ativo
                          ? 'border-marca bg-marca-suave text-marca-forte'
                          : 'border-borda bg-superficie text-tinta'
                      }`}
                    >
                      {c.hex && (
                        <span
                          aria-hidden
                          className="h-3.5 w-3.5 rounded-full border border-[rgba(0,0,0,0.12)]"
                          style={{ background: c.hex }}
                        />
                      )}
                      {c.cor}
                    </button>
                  )
                })}
              </div>

              <p className="mb-2.5 text-[12.5px] font-semibold text-texto">Tamanho</p>
              <div className="mb-5 flex flex-wrap gap-2">
                {ficha.tamanhos.map((t, i) => {
                  const celulaDoTamanho = corObjeto?.celulas[i]
                  const indisponivel =
                    !celulaDoTamanho || !celulaDoTamanho.existe || celulaDoTamanho.saldo === 0
                  const ativo = t === tamanho
                  return (
                    <button
                      key={t}
                      type="button"
                      disabled={indisponivel}
                      aria-pressed={ativo}
                      aria-label={`Tamanho ${t}`}
                      onClick={() => aoTrocarTamanho(t)}
                      className={`min-w-[58px] rounded-sistema border px-3 py-2 text-center ${
                        ativo
                          ? 'border-marca bg-marca-suave'
                          : indisponivel
                            ? 'cursor-not-allowed border-borda bg-superficie-suave'
                            : 'border-borda bg-superficie'
                      }`}
                    >
                      <div
                        className={`tabular text-[13.5px] font-bold ${
                          indisponivel ? 'text-inativo' : ativo ? 'text-marca-forte' : 'text-tinta'
                        }`}
                      >
                        {t}
                      </div>
                      <div
                        className={`tabular text-[10.5px] ${
                          !celulaDoTamanho || !celulaDoTamanho.existe
                            ? 'text-inativo'
                            : celulaDoTamanho.saldo === 0
                              ? 'text-erro'
                              : 'text-texto-suave'
                        }`}
                      >
                        {!celulaDoTamanho || !celulaDoTamanho.existe
                          ? '—'
                          : celulaDoTamanho.saldo === 0
                            ? 'zerado'
                            : `${celulaDoTamanho.saldo} un`}
                      </div>
                    </button>
                  )
                })}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <p className="mb-2 text-[12.5px] font-semibold text-texto">Quantidade</p>
                  <div className="inline-flex items-center overflow-hidden rounded-sistema border border-borda">
                    <button
                      type="button"
                      aria-label="Diminuir quantidade"
                      disabled={!celula}
                      onClick={aoDecrementar}
                      className="flex h-9 w-9 items-center justify-center text-texto disabled:text-inativo"
                    >
                      −
                    </button>
                    <span className="tabular flex h-9 w-11 items-center justify-center border-x border-borda text-[14px] font-bold">
                      {quantidade}
                    </span>
                    <button
                      type="button"
                      aria-label="Aumentar quantidade"
                      disabled={!celula || quantidade >= celula.saldo}
                      onClick={aoIncrementar}
                      className="flex h-9 w-9 items-center justify-center text-texto disabled:text-inativo"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="flex-1" />
                <div className="text-right">
                  <p className="text-[12px] text-texto-suave">Subtotal do item</p>
                  <p className="tabular text-[22px] font-bold text-tinta">
                    {formatarMoeda(celula ? celula.preco_centavos * quantidade : 0)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2.5 border-t border-borda bg-superficie-suave p-4">
          <Botao variante="secundario" porte="destaque" onClick={aoFechar}>
            Cancelar
          </Botao>
          <div className="flex-1" />
          <Botao
            porte="destaque"
            icone="apparel"
            disabled={!podeAdicionar}
            onClick={aoAdicionar}
          >
            {!tamanho
              ? 'Escolha o tamanho'
              : podeAdicionar
                ? 'Adicionar à condicional'
                : semCodigoBarras
                  ? 'Sem código de barras'
                  : 'Sem estoque'}
          </Botao>
        </div>
      </div>
    </>
  )
}
